import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiDatabase, FiBell, FiCheckCircle, FiCloud } = FiIcons;

const Settings = () => {
  const [settings, setSettings] = useState({
    lowStockThreshold: 10,
    criticalStockThreshold: 5,
    emailNotifications: true,
    browserNotifications: true,
    autoBackup: true,
    backupFrequency: 'daily'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = () => {
    localStorage.setItem('articleManagementSettings', JSON.stringify(settings));
    toast.success('Settings saved successfully!');
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Configure your article management preferences</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiBell} className="w-5 h-5 mr-2" />
            Alert Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Low Stock Threshold
              </label>
              <input
                type="number"
                name="lowStockThreshold"
                value={settings.lowStockThreshold}
                onChange={handleChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                Alert when articles remaining is below this number
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Critical Stock Threshold
              </label>
              <input
                type="number"
                name="criticalStockThreshold"
                value={settings.criticalStockThreshold}
                onChange={handleChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                Critical alert when articles remaining is below this number
              </p>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="emailNotifications"
                checked={settings.emailNotifications}
                onChange={handleChange}
                className="mr-2"
              />
              <label className="text-sm text-gray-700">Enable email notifications</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="browserNotifications"
                checked={settings.browserNotifications}
                onChange={handleChange}
                className="mr-2"
              />
              <label className="text-sm text-gray-700">Enable browser notifications</label>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiDatabase} className="w-5 h-5 mr-2" />
            Data Management
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-success-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Storage Status:</p>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCloud} className="w-5 h-5 text-success-500" />
                  <span className="text-sm font-medium text-success-600">Firebase Connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                All data is stored in Firebase Cloud Firestore
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex justify-end"
      >
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiSave} className="w-5 h-5" />
          <span>Save Settings</span>
        </button>
      </motion.div>
    </div>
  );
};

export default Settings;